import type { Role } from '@ithelpdesk/shared';

/** Most-privileged first — the identity line shows the one role that best describes the person. */
export const ROLE_ORDER: readonly Role[] = ['ADMIN', 'TEAM_LEAD', 'TECHNICIAN', 'AUDITOR', 'END_USER'];

const ROLE_LABELS: Record<Role, string> = {
  ADMIN: 'IT Administrator',
  TEAM_LEAD: 'Team Lead',
  TECHNICIAN: 'IT Technician',
  AUDITOR: 'Auditor',
  END_USER: 'Employee',
};

export function roleLabel(role: Role): string {
  return ROLE_LABELS[role] ?? role;
}

/** Display-only. Never used for an access decision — the server decides that. */
export function primaryRoleLabel(roles: readonly Role[]): string | null {
  const top = ROLE_ORDER.find((r) => roles.includes(r));
  return top ? roleLabel(top) : null;
}

/** The most-privileged role in `roles` (ROLE_ORDER), or null when there are none. Display-only, like `primaryRoleLabel`. */
export function primaryRole(roles: readonly Role[]): Role | null {
  return ROLE_ORDER.find((r) => roles.includes(r)) ?? null;
}

/** `roles` sorted most-privileged first. Display-only. */
export function sortRoles(roles: readonly Role[]): Role[] {
  return ROLE_ORDER.filter((r) => roles.includes(r));
}

/** A custom role as the session reports it (display only). */
export interface CustomRoleRef {
  name: string;
}

/**
 * The identity line under the person's name: their most-privileged system
 * role, or — when they hold no system role — the first custom role's name.
 * Display-only, like `primaryRoleLabel`.
 */
export function identityRoleLabel(roles: readonly Role[], customRoles: readonly CustomRoleRef[] | undefined): string | null {
  return primaryRoleLabel(roles) ?? customRoles?.[0]?.name ?? null;
}
